import { Alert, AlertColor } from "@mui/material"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { ConventionLocationStyle, localTimezone, MomentFormats } from "app-constants/constants"
import { createPath, ROUTE } from "app-constants/routing"
import { ActivityIndicator } from "components/activity-indicator"
import AppLink from "components/navigation/AppLink"
import { Text } from "components/text"
import useHeadingStyles from "hooks/useHeadingStyles"
import useRelativeTime from "hooks/useRelativeTime"
import { api } from "lib/api"
import useCurrent from "lib/use-current"
import Convention from "models/convention-api/v1/Convention"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import moment from "moment-timezone"
import { useRouter } from "next/router"
import { SnackbarKey, useSnackbar } from "notistack"
import { singular } from "pluralize"
import React, { useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui-compat"

const useStyles = makeStyles()({
  root: {
    display: "flex",
    margin: "20px 0",
    padding: "40px 30px 10px 30px",
    flexDirection: "column",
    width: "100%",
    justifyContent: "center",
    alignItems: "space-between",
    flexWrap: "wrap",
    background: "linear-gradient(180deg, rgba(242,242,243,0.5) 0%, #F2F2F3 100%, #63A4FF 100%)",
    borderRadius: "10px",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: "50px",
  },
  titleWrapper: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  titleBox: {
    display: "flex",
    flexDirection: "column",
  },
  subTitle: {
    color: "#556064",
    fontFamily: "Lato",
    fontSize: "16px",
    letterSpacing: "0",
    lineHeight: "19px",
  },
  eventButton: {
    height: "40px",
    width: "250px",
    borderRadius: "10px",
  },
  sessionDate: {
    color: "#556064",
    fontFamily: "Lato",
    fontSize: "14px",
    letterSpacing: 0,
    lineHeight: "17px",
  },
  bold: {
    fontWeight: "bold",
  },
  removeButton: {
    boxSizing: "border-box",
    height: "36px",
    width: "90px",
    border: "1px solid #0470C8",
    borderRadius: "5px",
    color: "#0470CB",
  },
})

interface IEvent {
  convention: Convention
}

const Event = (props: IEvent) => {
  const { convention } = props
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const current = useCurrent()

  const { reload } = useRouter()
  const [unscheduledConventionSessions, setUnscheduledConventionSessions] = useState<
    Record<string, boolean>
  >({})
  const [unscheduledConventionSessionsCounter, setUnscheduledConventionSessionsCounter] =
    useState(0)
  // @ts-ignore
  const { config: providerConfig } = useCurrent()

  const { classes } = useStyles()
  const { classes: headingClasses } = useHeadingStyles()
  const conventionSessions = ConventionSession.useAll(() => {
    if (!current?.user?.id) return null

    return {
      filter: {
        convention_id: convention.id,
        convention_user_id: current.user.id,
      },
      includes: ["convention_exhibitor"],
    }
  })

  const isExpired = convention.end_at.isBefore(moment())
  const formattedStartTime = convention.start_at.format(MomentFormats.startTime)
  const formattedEndTime = convention.end_at.format(MomentFormats.endTime)

  const showSnackbar = (message: string, severity: AlertColor = "info") => {
    enqueueSnackbar(message, {
      autoHideDuration: 6000,
      content: (key: SnackbarKey) => (
        <Alert onClose={() => onCloseSnackbar(key)} severity={severity}>
          {message}
        </Alert>
      ),
    })
  }

  const onCloseSnackbar = (key: SnackbarKey) => {
    closeSnackbar(key)
    reload()
  }

  const sortedConventionSession: ConventionSession[] = useMemo(() => {
    if (!conventionSessions.data) return []
    if (conventionSessions.data.length === 0) return []

    return conventionSessions.data.sort((sessionA, sessionB) => {
      return sessionA.start_at < sessionB.start_at ? 1 : -1
    })
  }, [conventionSessions.data])

  const onSessionRemovePress = (session: ConventionSession) => async () => {
    try {
      setUnscheduledConventionSessions((prevState) => ({ ...prevState, [session.id]: true }))
      setUnscheduledConventionSessionsCounter((prevState) => prevState + 1)
      await api.deleteSessionRegistration({ req: undefined }, session.id)
      showSnackbar("Successfully removed", "success")
    } catch (error) {
      console.error(error)
      showSnackbar("Something went wrong!", "error")
    }
  }

  const isLoadingConventionSessions =
    conventionSessions.data === undefined && conventionSessions.isValidating

  const getGoToConventionHref = () => {
    if (convention.location_style === ConventionLocationStyle.INPERSON) {
      return createPath({ path: ROUTE.CONVENTION, params: { conventionId: convention.id } })
    }

    return createPath({ path: ROUTE.CONVENTION_SESSIONS, params: { conventionId: convention.id } })
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleWrapper}>
          <Box className={classes.titleBox}>
            <Typography variant="h2" className={headingClasses.h4}>
              {convention.name}
            </Typography>
            <Typography className={classes.subTitle}>
              {`${formattedStartTime} - ${formattedEndTime}`}
            </Typography>
          </Box>
          {!isExpired && (
            <Box
              display="flex"
              justifyContent={["flex-end"]}
              width={["100%", "40%", "20%"]}
              mt={[4, 2, 0]}>
              <AppLink
                href={getGoToConventionHref()}
                color="primary"
                fullWidth
                className={classes.eventButton}
                variant="contained">
                {`Go to ${singular(providerConfig.expo_copy_text)}`}
              </AppLink>
            </Box>
          )}
        </Box>
      </Box>
      {isLoadingConventionSessions && <ActivityIndicator />}
      {sortedConventionSession.length > 0 &&
        sortedConventionSession.length > unscheduledConventionSessionsCounter && (
          <Box display="flex" flexDirection="column" width={["100%"]}>
            <Box display="flex" width={["100%"]}>
              <Typography component="h3">
                <Text text="Session Registrations" preset="bodyLargePrimary" />
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection={["column", "column", "row"]}
              flexWrap={["wrap"]}
              justifyContent={["center", "center", "flex-start"]}
              alignItems={["flex-start"]}
              width={["100%"]}
              mt={[4]}>
              {sortedConventionSession.map((session) => {
                // TODO: Refactor it! It's a temporary fix, to prevent `nested hooks error`
                function Component() {
                  const endAt = moment(session.end_at)
                  const startAt = moment(session.start_at)

                  const sessionRelativeTime = useRelativeTime({ startAt, endAt })

                  const isSessionLive = sessionRelativeTime.during
                  const isSessionInFuture = sessionRelativeTime.before
                  const isSessionInPast = sessionRelativeTime.after

                  const formattedDay = moment(session.start_at).format(
                    MomentFormats.dateOnlyWithoutYear,
                  )

                  const formattedStartTime = moment(session.start_at).format(MomentFormats.timeOnly)

                  const formattedEndTime = moment(session.end_at)
                    .tz(localTimezone)
                    .format(MomentFormats.timeOnlyWithZone)

                  if (unscheduledConventionSessions[session.id]) return null

                  return (
                    <Box style={{ marginBottom: 40, width: "100%" }}>
                      <Box
                        display="flex"
                        width={["100%"]}
                        justifyContent="space-between"
                        flexWrap="wrap">
                        <Box display="flex" flexDirection="column" width={["100%", "100%", "25%"]}>
                          <Typography component="h4">
                            <Text
                              preset="bodyMediumPrimary"
                              text={session?.convention_exhibitor?.exhibitor_name}
                            />
                          </Typography>
                        </Box>
                        <Box display="flex" flexDirection="column" width={["60%", "40%", "25%"]}>
                          <Typography className={classes.sessionDate}>
                            <span className={classes.bold}>{formattedDay} |</span>{" "}
                            {formattedStartTime} - {formattedEndTime}
                          </Typography>
                          <Typography component="h4">
                            <Text preset="bodyMediumPrimary" text={session.name} />
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          justifyContent={["flex-end", "center", "center"]}
                          alignItems={["flex-start", "center"]}
                          width={["30%", "30%", "20%"]}>
                          {isSessionInFuture && (
                            <Button
                              color="primary"
                              variant="outlined"
                              onClick={onSessionRemovePress(session)}
                              className={classes.removeButton}>
                              Remove
                            </Button>
                          )}
                        </Box>

                        <Box
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          width={["100%", "200px", "200px"]}
                          mt={[3, 2, 0]}>
                          {isSessionLive && (
                            <AppLink
                              href={createPath({
                                path: ROUTE.CONVENTION_SESSION,
                                params: { sessionId: session.id, conventionId: convention.id },
                              })}
                              variant="outlined">
                              Join {providerConfig.session_copy_text}
                            </AppLink>
                          )}
                          {isSessionInFuture && (
                            <Text
                              preset="bodySmallPrimary"
                              text={`Session starts ${moment(session.start_at).fromNow()}!`}
                              style={{
                                textAlign: "left",
                                fontStyle: "italic",
                              }}
                            />
                          )}
                          {isSessionInPast && (
                            <Text
                              preset="bodySmallPrimary"
                              text={`This session has ended.`}
                              style={{
                                textAlign: "left",
                                fontStyle: "italic",
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )
                }
                return <Component key={`convention-session-${session.id}`} />
              })}
            </Box>
          </Box>
        )}
    </Box>
  )
}

export default Event
